import {
  LocalizedText,
  ReleaseEntry,
  ReleaseEntryWording,
  ReleaseLocale,
  ReleaseSurface,
  ResolvedReleaseEntry,
  ResolvedReleaseStory,
  releaseLocales,
} from './types';

function isReleaseLocale(locale: string): locale is ReleaseLocale {
  return (releaseLocales as readonly string[]).includes(locale);
}

/** Maps any client locale to a release locale. Strip the region. An unknown locale gives `en`. */
export function toReleaseLocale(locale: string): ReleaseLocale {
  const base = locale.trim().toLowerCase().split(/[-_]/)[0] ?? '';
  return isReleaseLocale(base) ? base : 'en';
}

/** English is the fallback. A missing or empty translation shows English, never an empty string. */
export function resolveText(text: LocalizedText, locale: string): string {
  const value = text[toReleaseLocale(locale)];
  return value !== undefined && value.trim() !== '' ? value : text.en;
}

type StoryField = 'tag' | 'headline' | 'lede' | 'before' | 'now' | 'note';

function resolveStory(entry: ReleaseEntry, surface: ReleaseSurface, locale: string): ResolvedReleaseStory | undefined {
  const { story } = entry;
  if (!story) {
    return undefined;
  }
  const overrides: Record<ReleaseSurface, ReleaseEntryWording['story']> = {
    web: undefined,
    ios: entry.ios?.story,
    android: entry.android?.story,
  };
  const override = overrides[surface];
  const pick = (field: StoryField): string | undefined => {
    const text = override?.[field] ?? story[field];
    return text ? resolveText(text, locale) : undefined;
  };
  const resolved: ResolvedReleaseStory = {
    tag: pick('tag') ?? '',
    headline: pick('headline') ?? '',
    lede: pick('lede') ?? '',
    accent: story.accent,
  };
  const before = pick('before');
  const now = pick('now');
  const note = pick('note');
  // Emit before and now only as a pair.
  if (before && now) {
    resolved.before = before;
    resolved.now = now;
  }
  if (note) {
    resolved.note = note;
  }
  if (story.image) {
    resolved.image = story.image;
  }
  return resolved;
}

/**
 * Resolves one entry for one surface and one locale. A surface override falls back to its own
 * English, never to the translation of the web wording, which can name the wrong platform.
 */
export function resolveEntry(
  entry: ReleaseEntry,
  sourceVersion: string,
  surface: ReleaseSurface,
  locale: string
): ResolvedReleaseEntry {
  const overrides: Record<ReleaseSurface, ReleaseEntry['ios']> = {
    web: undefined,
    ios: entry.ios,
    android: entry.android,
  };
  const override = overrides[surface];
  const resolved: ResolvedReleaseEntry = {
    id: `${sourceVersion}-${entry.id}`,
    sourceVersion,
    category: entry.category,
    highlight: entry.highlight === true,
    title: resolveText(override?.title ?? entry.title, locale),
    body: resolveText(override?.body ?? entry.body, locale),
  };
  const story = resolveStory(entry, surface, locale);
  if (story) {
    resolved.story = story;
  }
  return resolved;
}
